import React, { useEffect } from "react";
import { ConstraintV2, ConstraintV2Draft } from "../../../types/adapter";
import { Platform } from "../../../types/platform";
import { Form, Input, Modal, Select } from "antd";

export interface ConstraintV2FormProps {
  open: boolean;
  record?: ConstraintV2;
  platform?: Platform | string;
  onSave: (record: ConstraintV2Draft) => void;
  onCancel: () => void;
}

// Platform not yet known (adapter still loading) — offer every source rather than guessing wrong.
// **Corrected 2026-09-22** (double-review 2026-09-22 R7c): that comment/behavior was wrong — the
// backend only accepts "manual" for an UNKNOWN platform, so offering the full cross-platform list
// here let the form submit a combination the API would reject. The Source `Select` is also now
// disabled while `platform` is undefined, so a user can't pick from this list before it's known.
const sourceOptions = (platform?: Platform | string) => {
  if (platform === Platform.IOS) {
    return [{ value: "cocoapods" }, { value: "spm" }, { value: "manual" }];
  }
  if (platform === Platform.ANDROID) {
    return [{ value: "maven" }, { value: "manual" }];
  }
  return [{ value: "manual" }];
};

/**
 * `POST /v4/adapter/{id}/constraints/v2` always creates a "manual" row — the field is
 * implicit and never shown/submitted here, unlike crawler-derived rows which never go
 * through this form.
 *
 * **Corrected 2026-09-22** (adapter full-CRUD task, user-requested scope addition): the claim
 * above is wrong — `source` is now a required field on this form for both create and edit
 * (defaulting to "manual" on create), with options scoped to the adapter's platform, and is sent
 * explicitly in the POST/PUT body rather than left implicit.
 */
export const ConstraintV2Form = (props: ConstraintV2FormProps) => {
  const [form] = Form.useForm<ConstraintV2Draft>();
  const { record, open } = props;

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(record ? record : { source: "manual" } as ConstraintV2Draft);
  }, [record, open, form]);

  return (
    <Modal
      open={props.open}
      title={record ? "Edit Constraint" : "Add Constraint"}
      okText={record ? "Save" : "Add"}
      cancelText="Cancel"
      onCancel={props.onCancel}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            props.onSave(values);
          })
          .catch((info) => {
            console.log("Validate Failed:", info);
          });
      }}
    >
      <Form form={form} layout="vertical" name="constraint_v2_form">
        <Form.Item
          name="dependencyKey"
          label="Dependency Key"
          rules={[
            { required: true, message: "Please input the dependency key!" },
          ]}
        >
          <Input aria-label="Dependency key" />
        </Form.Item>
        <Form.Item
          name="version"
          label="Version"
          rules={[{ required: true, message: "Please input the version!" }]}
        >
          <Input aria-label="Version" />
        </Form.Item>
        <Form.Item
          name="operator"
          label="Operator"
          rules={[{ required: true, message: "Please select operator!" }]}
        >
          <Select
            aria-label="Operator"
            options={[
              { value: "==" },
              { value: ">" },
              { value: ">=" },
              { value: "<" },
              { value: "<=" },
              { value: "~>" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="source"
          label="Source"
          rules={[{ required: true, message: "Please select source!" }]}
        >
          <Select
            aria-label="Source"
            disabled={props.platform === undefined}
            options={sourceOptions(props.platform)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
